import "../styles.css"
import "@phosphor-icons/web/regular"
import van from "vanjs-core"
import { is_standalone } from "../lib/pwa.client"
import pkg from "../../package.json"
import { navigate } from "../lib/router.client"
import { copy_to_clipboard, mcp_url, set_workspace, workspace } from "../lib/workspace.client"
import { session } from "../lib/session.client"

const t = van.tags

// ─────────────────────────────────────────────
// Icon Phosphor — <i class="ph ph-<name>">
// ─────────────────────────────────────────────
function Icon(name: string, cls = "text-2xl") {
	return t.i({ class: `ph ph-${name} ${cls}` })
}

// Mapăm numele logice la iconuri Phosphor (https://phosphoricons.com)
const ICONS = {
	shield: "shield-check",
	sparkles: "sparkle",
	doc: "file-text",
	stack: "stack",
	map: "map-trifold",
	lock: "lock-key",
	clock: "clock-counter-clockwise",
	cube: "cube",
}

// Scroll lin la o secțiune din pagină. preventDefault oprește navigarea hash
// (altfel router-ul ar interpreta #valoare ca rută); fără JS, ancora face fallback nativ.
function scroll_to_id(id: string) {
	return (e: Event) => {
		e.preventDefault()
		document.getElementById(id)?.scrollIntoView({ behavior: "smooth" })
	}
}

// ─────────────────────────────────────────────
// Componente de prezentare reutilizabile
// ─────────────────────────────────────────────
function Eyebrow(text: string, cls = "text-cyan-600") {
	return t.div({ class: `${cls} font-semibold tracking-wider uppercase text-xs mb-3` }, text)
}

function SectionHead(eyebrow: string, title: string, sub?: string) {
	return t.div(
		{ class: "max-w-2xl mb-12" },
		Eyebrow(eyebrow),
		t.h2(
			{ class: "text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100" },
			title,
		),
		sub ? t.p({ class: "mt-4 text-lg text-slate-600 dark:text-slate-300" }, sub) : null,
	)
}

function ValueCard(icon: string, accent: string, title: string, body: string) {
	return t.div(
		{
			class:
				"rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-7 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition",
		},
		t.div(
			{ class: `inline-flex items-center justify-center w-12 h-12 rounded-xl ${accent} mb-5` },
			Icon(icon),
		),
		t.h3({ class: "text-lg font-bold text-slate-900 dark:text-slate-100" }, title),
		t.p({ class: "mt-2 text-slate-600 dark:text-slate-300 leading-relaxed" }, body),
	)
}

function PersonaCard(role: string, pain: string, message: string) {
	return t.div(
		{
			class:
				"rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6",
		},
		t.div({ class: "font-bold text-slate-900 dark:text-slate-100" }, role),
		t.div(
			{
				class:
					"mt-3 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500",
			},
			"Ce-l doare",
		),
		t.p({ class: "text-slate-600 dark:text-slate-300 text-sm mt-1" }, pain),
		t.div(
			{
				class:
					"mt-4 text-xs font-semibold uppercase tracking-wide text-cyan-700 dark:text-cyan-300",
			},
			"Ce primește",
		),
		t.p({ class: "text-slate-800 dark:text-slate-200 text-sm mt-1 font-medium" }, message),
	)
}

function Capability(num: string, icon: string, title: string, body: string) {
	return t.div(
		{ class: "flex gap-5" },
		t.div(
			{
				class:
					"flex-shrink-0 w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center",
			},
			Icon(icon),
		),
		t.div(
			t.div(
				{ class: "flex items-center gap-2" },
				t.span({ class: "text-cyan-700 dark:text-cyan-300 font-mono text-sm font-bold" }, num),
				t.h3({ class: "text-lg font-bold text-slate-900 dark:text-slate-100" }, title),
			),
			t.p({ class: "mt-1 text-slate-600 dark:text-slate-300 leading-relaxed" }, body),
		),
	)
}

// Întrebare-exemplu pentru asistent: întrebarea cu ghilimele + ce primești înapoi.
function QuestionCard(question: string, answer: string) {
	return t.div(
		{
			class:
				"rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6",
		},
		t.p(
			{ class: "text-slate-900 dark:text-slate-100 font-semibold leading-snug" },
			`„${question}”`,
		),
		t.p({ class: "mt-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed" }, answer),
	)
}

// Badge versiune + DEMO (dublat în AppHeader din app/app.client.ts).
function VersionBadge() {
	return t.span(
		{
			class:
				// hidden sm: pe telefon nu încape lângă butonul de Autentificare + CTA (feedback 25.08)
				"hidden sm:inline-flex items-center whitespace-nowrap rounded-full bg-amber-100 dark:bg-amber-950 text-amber-800 dark:text-amber-300 px-2 py-0.5 " +
				"text-[10px] font-bold tracking-wide",
		},
		`v${pkg.version}`,
	)
}

// URL-ul public al platformei: pe deploy folosim origin-ul curent; de pe localhost, producția.
function app_url(): string {
	return location.hostname === "localhost" ? "https://urbanism.vego.holdings" : location.origin
}

// Mesaj scurt de prezentare, gata de lipit într-o conversație (* = bold în aplicațiile de chat).
function mesaj_scurt(): string {
	return `Cât durează astăzi un Certificat de Urbanism în primăria dumneavoastră? Săptămâni?

*Urbanism by VEGO* îl face în minute: PUG-ul devine hartă digitală, parcela se găsește cu o întrebare în limba română sau după adresă, zonele de protecție (rețele, monumente, situri) se văd direct pe hartă, iar documentul se completează automat și intră în registru. Omul verifică și semnează.

PUG-ul digital e deja obligație legală (Ordinul MDLPA 904/2023) și finanțabil prin programele de digitalizare (Programele Regionale 2021–2027, #DigiLocal). Platforma, live: ${app_url()}`
}

// Adresă de înaintare — text formal de email, care vinde prin capabilități + beneficii.
function adresa_inaintare(): string {
	return `Bună ziua,

Vă prezentăm Urbanism by VEGO — soluția dedicată gestionării documentațiilor de urbanism și emiterii actelor.

Ce puteți face cu platforma:
— găsiți orice parcelă întrebând în limba română sau căutând după adresă ori număr cadastral, cu harta deschisă exact pe ea;
— verificați pe loc zona (UTR), indicatorii (POT/CUT), restricțiile și zonele de protecție — rețele edilitare, monumente istorice, situri — vizibile direct pe hartă;
— emiteți Certificate de Urbanism și Autorizații de construire completate automat cu datele parcelei, din șabloanele instituției dumneavoastră;
— evidența se ține singură: fiecare act intră în registrul electronic, legat de parcela lui, cu istoric complet și cu dovada datelor folosite.

Beneficiile pentru instituție: actele ies în minute în loc de săptămâni, erorile de transcriere dispar, orice document poate fi probat și peste ani, iar echipa nu are nevoie de pregătire tehnică — funcționarul rămâne cel care verifică, decide și semnează.

Formatul digital al PUG-ului este deja o obligație legală (Ordinul MDLPA nr. 904/2023), iar proiectul se poate finanța prin Programele Regionale 2021–2027 sau prin programe naționale precum #DigiLocal. Implementarea durează două zile atunci când PUG-ul există în format GIS; datele rămân ale instituției, găzduite în Uniunea Europeană.

Platforma poate fi văzută live la ${app_url()}.

Cu deosebită considerație,
Echipa Urbanism by VEGO
office@vego.holdings`
}

// Buton generic „copiază în clipboard" cu confirmare temporară (pattern-ul butonului MCP).
function CopyTextButton(idle_label: string, make_text: () => string) {
	const label = van.state(idle_label)
	return t.button(
		{
			class:
				"inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-cyan-300/40 " +
				"text-cyan-100 text-sm font-medium hover:bg-white/10 transition cursor-pointer",
			onclick: async () => {
				const ok = await copy_to_clipboard(make_text())
				label.val = ok ? "Copiat ✓" : "Eroare la copiere"
				setTimeout(() => {
					label.val = idle_label
				}, 2000)
			},
		},
		() => label.val,
	)
}

// ─────────────────────────────────────────────
// Alegerea workspace-ului: hărțile existente, drept carduri.
// Click = setează workspace-ul global și intră pe hartă.
// ─────────────────────────────────────────────

interface MapCard {
	slug: string
	name: string
	description: string | null
}

const maps_state = van.state<MapCard[] | null>(null) // null = încă se încarcă

async function load_maps_cards() {
	try {
		const res = await fetch("/api/maps")
		const body = (await res.json()) as { ok: boolean; data?: MapCard[] }
		maps_state.val = body.ok && Array.isArray(body.data) ? body.data : []
	} catch {
		maps_state.val = []
	}
}

function WorkspacePicker() {
	return t.section(
		{
			id: "workspace",
			class: "border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900",
		},
		t.div(
			{ class: "max-w-6xl mx-auto px-6 py-14" },
			Eyebrow("Spațiul de lucru"),
			t.h2(
				{
					class: "text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100",
				},
				"Alegeți harta de lucru",
			),
			t.p(
				{ class: "mt-2 text-slate-600 dark:text-slate-300" },
				"Tot ce urmează — harta, documentele, registrele — e în contextul hărții alese.",
			),
			() => {
				const maps = maps_state.val
				if (maps === null) {
					return t.p(
						{ class: "mt-6 text-sm text-slate-400 dark:text-slate-500 animate-pulse" },
						"Se încarcă hărțile…",
					)
				}
				if (maps.length === 0) {
					return t.p(
						{ class: "mt-6 text-sm text-slate-500 dark:text-slate-400" },
						"Nicio hartă încă — importați una din Administrare.",
					)
				}
				return t.div(
					{ class: "mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" },
					...maps.map((m) => {
						const copy_label = van.state("⧉ Link pentru asistentul AI")
						return t.div(
							{
								class: () =>
									"text-left rounded-2xl border p-6 transition hover:shadow-md hover:-translate-y-0.5 cursor-pointer " +
									(workspace.val.slug === m.slug
										? "border-cyan-600 bg-cyan-50/60 dark:bg-cyan-950/40 ring-1 ring-cyan-600/30"
										: "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"),
								onclick: () => {
									set_workspace(m.slug, m.name)
									navigate("/map")
								},
							},
							t.div(
								{ class: "flex items-center justify-between gap-2" },
								t.h3({ class: "font-bold text-slate-900 dark:text-slate-100" }, m.name || m.slug),
								() =>
									t.span(
										{
											class:
												"text-[11px] font-semibold px-2 py-0.5 rounded-full " +
												(workspace.val.slug === m.slug
													? "bg-cyan-700 text-white"
													: "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400"),
										},
										workspace.val.slug === m.slug ? "activă" : "deschide →",
									),
							),
							m.description
								? t.p(
										{ class: "mt-2 text-sm text-slate-600 dark:text-slate-300 line-clamp-2" },
										m.description,
									)
								: t.p({ class: "mt-2 text-sm text-slate-400 dark:text-slate-500" }, m.slug),
							// Link de conectare a asistentului AI la această hartă (MCP).
							// stopPropagation: butonul NU selectează workspace-ul.
							t.button(
								{
									class:
										"mt-4 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 " +
										"bg-slate-50 dark:bg-slate-950 text-[11px] font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 " +
										"hover:text-slate-900 transition",
									title: () => `Copiază ${mcp_url(m.slug)}`,
									onclick: async (e: Event) => {
										e.stopPropagation()
										const ok = await copy_to_clipboard(mcp_url(m.slug))
										copy_label.val = ok ? "Copiat ✓" : "Eroare la copiere"
										setTimeout(() => {
											copy_label.val = "⧉ Link pentru asistentul AI"
										}, 1500)
									},
								},
								() => copy_label.val,
							),
						)
					}),
				)
			},
		),
	)
}

// ─────────────────────────────────────────────
// Pagina
// ─────────────────────────────────────────────
export function LandingPage() {
	queueMicrotask(load_maps_cards)
	return t.div(
		{
			class:
				"min-h-screen bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 antialiased",
		},

		// ── Nav ──
		t.header(
			{
				class:
					"sticky top-0 z-30 backdrop-blur bg-white/80 dark:bg-slate-900/80 border-b border-slate-200 dark:border-slate-700",
			},
			t.nav(
				{ class: "max-w-6xl mx-auto px-6 h-16 flex items-center justify-between" },
				t.a(
					{ href: "#/", class: "flex items-center gap-2 flex-shrink-0" },
					t.img({ src: "/public/logo-192.png", alt: "Urbanism", class: "w-8 h-8" }),
					t.span(
						{ class: "text-cyan-700 dark:text-cyan-300 font-extrabold text-xl tracking-tight" },
						"Urbanism",
					),
					t.span(
						{
							class:
								"hidden xl:inline whitespace-nowrap text-slate-400 dark:text-slate-500 text-sm",
						},
						"urbanism digital",
					),
					VersionBadge(),
				),
				t.div(
					{
						class:
							"hidden lg:flex items-center gap-5 xl:gap-7 whitespace-nowrap text-sm font-medium text-slate-600 dark:text-slate-300",
					},
					t.a(
						{
							href: "#cum-functioneaza",
							onclick: scroll_to_id("cum-functioneaza"),
							class: "hover:text-slate-900",
						},
						"Cum funcționează",
					),
					t.a(
						{ href: "#valoare", onclick: scroll_to_id("valoare"), class: "hover:text-slate-900" },
						"Valoare",
					),
					t.a(
						{
							href: "#pentru-cine",
							onclick: scroll_to_id("pentru-cine"),
							class: "hover:text-slate-900",
						},
						"Pentru cine",
					),
					t.a(
						{
							href: "#platforma",
							onclick: scroll_to_id("platforma"),
							class: "hover:text-slate-900",
						},
						"Platformă",
					),
				),
				t.div(
					{ class: "flex items-center gap-2 whitespace-nowrap flex-shrink-0" },
					t.a(
						{
							href: "#/documents",
							class:
								"hidden sm:inline px-3.5 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900",
						},
						"Documente",
					),
					t.a(
						{
							href: "#/admin",
							class:
								"hidden sm:inline px-3.5 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900",
						},
						"Administrare",
					),
					// Ușa EXPLICITĂ de login (feedback 25.08): înainte, autentificarea se găsea doar
					// prin ecranul de lacăt al linkurilor de staff — iar pe telefon nici acelea nu se
					// vedeau. Vizibilă pe TOATE lățimile; dispare după login (nod stabil, F8).
					t.a(
						{
							href: "#/autentificare",
							class: () =>
								"px-3.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 " +
								"text-sm font-semibold text-slate-700 dark:text-slate-200 " +
								"hover:bg-slate-100 dark:hover:bg-slate-800 transition" +
								(session.val.authenticated ? " hidden" : ""),
						},
						"Autentificare",
					),
					t.a(
						{
							href: "#/map",
							class:
								"px-4 py-2 rounded-lg bg-cyan-700 text-white text-sm font-semibold hover:bg-cyan-800",
						},
						// pe telefon nu încap ambele butoane cu text integral
						t.span({ class: "sm:hidden" }, "Harta"),
						t.span({ class: "hidden sm:inline" }, "Deschide harta"),
					),
				),
			),
		),

		// ── Hero ──
		t.section(
			{ class: "relative overflow-hidden" },
			t.div({
				class:
					"absolute inset-0 bg-gradient-to-b from-cyan-50 via-white to-white dark:from-cyan-950 dark:via-slate-900 dark:to-slate-900 pointer-events-none",
			}),
			t.div(
				{ class: "relative max-w-6xl mx-auto px-6 pt-20 pb-24 sm:pt-28 sm:pb-32" },
				t.div(
					{
						class:
							"inline-flex items-center gap-2 rounded-full border border-cyan-200 dark:border-cyan-800 bg-cyan-50 dark:bg-cyan-950 px-3.5 py-1.5 text-sm font-medium text-cyan-800 dark:text-cyan-300",
					},
					t.span({ class: "w-2 h-2 rounded-full bg-cyan-500" }),
					"Pentru primării și compartimentele de urbanism",
				),
				t.h1(
					{
						class:
							"mt-6 text-4xl sm:text-6xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100 max-w-3xl leading-[1.05]",
					},
					"Certificatul de Urbanism: ",
					t.span({ class: "text-cyan-700 dark:text-cyan-300" }, "în minute, nu în săptămâni"),
				),
				t.p(
					{
						class:
							"mt-6 text-lg sm:text-xl text-slate-600 dark:text-slate-300 max-w-2xl leading-relaxed",
					},
					"Harta digitală a PUG-ului, plus un asistent care răspunde la întrebări în limba română și completează documentele automat. ",
					t.b(
						{ class: "text-slate-800 dark:text-slate-200" },
						"Omul verifică, decide și semnează.",
					),
				),
				t.div(
					{ class: "mt-9 flex flex-col sm:flex-row gap-3" },
					t.a(
						{
							href: "#/map",
							class:
								"px-6 py-3.5 rounded-xl bg-cyan-700 text-white font-semibold text-center hover:bg-cyan-800 shadow-sm shadow-cyan-700/20",
						},
						"Vedeți harta interactivă",
					),
					t.a(
						{
							href: "#cere-demo",
							onclick: scroll_to_id("cere-demo"),
							class:
								"px-6 py-3.5 rounded-xl border border-slate-300 dark:border-slate-600 font-semibold text-slate-700 dark:text-slate-200 text-center hover:bg-slate-50 dark:hover:bg-slate-800",
						},
						"Cereți o demonstrație",
					),
				),
				t.p(
					{ class: "mt-6 text-sm text-slate-500 dark:text-slate-400" },
					"Datele rămân ale instituției · Omul semnează, întotdeauna · PUG-ul digital — obligație legală (Ordin MDLPA 904/2023)",
				),
			),
		),

		// ── CTA pilot (sus, înainte de spațiul de lucru) ──
		t.section(
			{ id: "cere-demo", class: "max-w-6xl mx-auto px-6 pb-20" },
			t.div(
				{
					class:
						"relative overflow-hidden rounded-3xl bg-gradient-to-br from-cyan-700 to-cyan-900 px-8 py-16 sm:px-16 text-center",
				},
				t.h2(
					{ class: "text-3xl sm:text-4xl font-bold text-white max-w-2xl mx-auto leading-tight" },
					"Deveniți primăria-pilot pentru urbanism digital",
				),
				t.p(
					{ class: "mt-4 text-cyan-100 text-lg max-w-xl mx-auto" },
					"Vă transpunem PUG-ul într-o hartă vie și vă arătăm platforma cu datele dumneavoastră reale — o demonstrație de 30 de minute.",
				),
				t.div(
					{ class: "mt-8 flex flex-col sm:flex-row gap-3 justify-center" },
					t.a(
						{
							href: "mailto:office@vego.holdings?subject=Demonstrație%20Urbanism",
							class:
								"px-6 py-3.5 rounded-xl bg-white dark:bg-slate-900 text-cyan-800 dark:text-cyan-300 font-semibold hover:bg-cyan-50 dark:hover:bg-cyan-950",
						},
						"Programați o demonstrație",
					),
					t.a(
						{
							href: "#/map",
							class:
								"px-6 py-3.5 rounded-xl border border-cyan-300/40 text-white font-semibold hover:bg-white/10",
						},
						"Explorați harta acum",
					),
				),
				// Pachetul concret al demonstrației — cumpărătorul știe exact ce primește.
				t.p(
					{ class: "mt-7 text-cyan-100/90 text-sm max-w-2xl mx-auto" },
					t.b({ class: "text-white" }, "Ce parcurgem în demonstrație: "),
					"importăm PUG-ul dumneavoastră (sau un set demonstrativ) · alegem o parcelă reală · verificăm zonele de protecție pe hartă · generăm un draft de Certificat de Urbanism · îl înregistrăm în registru · vă arătăm dovada datelor folosite.",
				),
				// Texte de dat mai departe: mesaj scurt (chat) + adresă de înaintare (email formal).
				t.div(
					{ class: "mt-5 flex flex-col sm:flex-row gap-3 justify-center" },
					CopyTextButton("⧉ Mesaj scurt de prezentare", mesaj_scurt),
					CopyTextButton("⧉ Adresă de înaintare (email)", adresa_inaintare),
				),
			),
		),

		// ── Workspace (alegerea hărții de lucru) ──
		WorkspacePicker(),

		// ── Problema ──
		t.section(
			{ class: "border-y border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950" },
			t.div(
				{ class: "max-w-6xl mx-auto px-6 py-14 grid sm:grid-cols-3 gap-8" },
				[
					[
						"Informația e îngropată",
						"Reglementările stau în planșe pe hârtie și PDF-uri scanate. O simplă verificare înseamnă zile de căutat prin arhivă.",
					],
					[
						"Transcrierea de mână bagă erori în acte",
						"Datele găsite se copiază manual în șablonul de document — iar erorile de copiere ajung în acte oficiale.",
					],
					[
						"Evidența nu poate proba nimic",
						"La o contestație, după un an, nimeni nu mai poate dovedi pe ce date s-a bazat actul emis. Registrele pe hârtie tac.",
					],
				].map(([h, p]) =>
					t.div(
						t.div({ class: "text-3xl font-extrabold text-slate-300" }, "—"),
						t.h3({ class: "mt-2 font-bold text-slate-900 dark:text-slate-100" }, h),
						t.p({ class: "mt-2 text-slate-600 dark:text-slate-300 text-sm leading-relaxed" }, p),
					),
				),
			),
		),

		// ── Cum functioneaza: fluxul unui CU ──
		t.section(
			{ id: "cum-functioneaza", class: "max-w-6xl mx-auto px-6 py-24" },
			SectionHead(
				"Cum funcționează",
				"Un Certificat de Urbanism, în patru pași",
				"De la cererea cetățeanului la documentul semnat — în aceeași zi, nu în săptămâni.",
			),
			t.div(
				{ class: "grid md:grid-cols-2 gap-x-10 gap-y-12" },
				Capability(
					"01",
					ICONS.map,
					"Găsiți parcela",
					"Întrebați în limba română — „găsește parcela cu numărul cadastral 1234” — iar asistentul o găsește și deschide harta exact pe ea. În browser, fără niciun program instalat.",
				),
				Capability(
					"02",
					ICONS.shield,
					"Verificați direct în plan",
					"Zona în care se află parcela, ce e permis să se construiască, indicatorii (POT, CUT), restricțiile și suprapunerile cu zonele de protecție. Verificate pe planul real, nu din memorie.",
				),
				Capability(
					"03",
					ICONS.doc,
					"Documentul se completează singur",
					"Șablonul Word al instituției se completează automat cu datele din hartă. Funcționarul completează doar ce ține de judecata lui, verifică și semnează. Fără transcriere de mână.",
				),
				Capability(
					"04",
					ICONS.stack,
					"Intră automat în registru",
					"Documentul emis se înregistrează singur: legat de parcela lui, cu număr de registru și cu tot istoricul — cine l-a emis, când și pe ce date s-a bazat.",
				),
			),
			// Evidențiere: zonele de protecție se VĂD pe hartă, nu se presupun.
			t.div(
				{
					class:
						"mt-14 rounded-2xl border border-cyan-200 dark:border-cyan-800 bg-cyan-50/60 dark:bg-cyan-950/40 p-7 sm:flex items-start gap-5",
				},
				t.div(
					{
						class:
							"flex-shrink-0 w-12 h-12 rounded-xl bg-cyan-700 text-white inline-flex items-center justify-center",
					},
					Icon(ICONS.map),
				),
				t.div(
					t.h3(
						{ class: "text-lg font-bold text-slate-900 dark:text-slate-100 mt-3 sm:mt-0" },
						"Zonele de protecție se văd direct pe hartă",
					),
					t.p(
						{ class: "mt-1 text-slate-600 dark:text-slate-300 leading-relaxed" },
						"Rețele edilitare (apă, canalizare, gaz, energie), monumente istorice, situri arheologice, ape și drumuri — fiecare cu zona ei de protecție, afișată peste parcele. Orice informație care are un loc pe hartă devine un strat vizibil: suprapunerile se văd dintr-o privire, nu se presupun.",
					),
				),
			),
		),

		// ── Asistentul: intrebari in limba romana ──
		t.section(
			{
				id: "asistent",
				class: "bg-slate-50 dark:bg-slate-950 border-y border-slate-200 dark:border-slate-700",
			},
			t.div(
				{ class: "max-w-6xl mx-auto px-6 py-24" },
				SectionHead(
					"Asistentul",
					"Întrebați în limba română",
					"Nu trebuie să știți să lucrați cu hărți sau cu baze de date — întrebați ca pe un coleg. Răspunsul vine din planul real, nu din amintiri.",
				),
				t.div(
					{ class: "grid sm:grid-cols-2 gap-5" },
					QuestionCard(
						"Ce regim de înălțime e pe strada Morii?",
						"Răspunsul vine din regulamentul planului, cu un link care deschide harta exact pe zona respectivă.",
					),
					QuestionCard(
						"Ce avize sunt necesare pentru o construcție lângă un monument?",
						"Asistentul cunoaște legislația în vigoare și răspunde pe situația concretă a parcelei, nu la modul general.",
					),
					QuestionCard(
						"Ce certificate am emis anul acesta pe strada Primăriei?",
						"Registrul documentelor e legat de hartă — evidența se întreabă, nu se răsfoiește prin dosare.",
					),
					QuestionCard(
						"Cum arăta planul în martie, când s-a emis certificatul?",
						"Nimic nu se pierde: starea planului de la orice dată se reconstituie exact — proba pentru orice contestație.",
					),
				),
				t.div(
					{
						class:
							"mt-8 rounded-xl border border-cyan-200 bg-cyan-50 dark:bg-cyan-950 p-5 text-slate-700 dark:text-slate-200",
					},
					t.b(
						{ class: "text-slate-900 dark:text-slate-100" },
						"Folosiți asistentul AI oferit de noi sau conectați-l pe cel pe care îl folosiți deja. ",
					),
					"Indiferent de alegere, datele rămân la instituție, iar fiecare acțiune a asistentului e semnată și poate fi anulată oricând.",
				),
			),
		),

		// ── Valoare ──
		t.section(
			{ id: "valoare", class: "max-w-6xl mx-auto px-6 py-24" },
			SectionHead(
				"De ce contează",
				"Trei motive pentru care primăriile aleg Urbanism",
				"Nu e doar un registru electronic și nu e doar o hartă: platforma interoghează PUG-ul digitizat, completează actele cu valorile parcelei și păstrează dovada valorilor folosite — pentru fiecare document.",
			),
			t.div(
				{ class: "grid md:grid-cols-3 gap-6" },
				ValueCard(
					ICONS.doc,
					"bg-cyan-100 dark:bg-cyan-950 text-cyan-700 dark:text-cyan-300",
					"Cerut de lege, finanțabil",
					"PUG-ul în format digital GIS e obligație legală — Ordinul MDLPA 904/2023, în aplicarea Legii 350/2001. Proiectul se poate finanța prin Programele Regionale 2021–2027 și programe naționale ca #DigiLocal.",
				),
				ValueCard(
					ICONS.shield,
					"bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300",
					"Orice act se poate proba",
					"Nimic nu se pierde, nimic nu se suprascrie: pentru fiecare modificare se știe cine, ce și când. La o contestație, dovada e la un click — și peste 5 ani.",
				),
				ValueCard(
					ICONS.sparkles,
					"bg-violet-100 dark:bg-violet-950 text-violet-700 dark:text-violet-300",
					"„Întreabă harta”",
					"Oricine din instituție poate întreba în limba română — „ce regim de înălțime e pe strada X?” — fără pregătire specială, fără cursuri.",
				),
			),
		),

		// ── Pentru cine ──
		t.section(
			{
				id: "pentru-cine",
				class: "bg-slate-50 dark:bg-slate-950 border-y border-slate-200 dark:border-slate-700",
			},
			t.div(
				{ class: "max-w-6xl mx-auto px-6 py-24" },
				SectionHead(
					"Pentru cine",
					"Fiecare om din primărie are de câștigat",
					"O singură platformă — fiecare rol primește altceva de la ea.",
				),
				t.div(
					{ class: "grid sm:grid-cols-2 lg:grid-cols-4 gap-5" },
					PersonaCard(
						"Primar / decident politic",
						"Imagine publică, transparență, atragerea de fonduri.",
						"Un oraș digital și transparent — finanțabil prin Programele Regionale sau #DigiLocal.",
					),
					PersonaCard(
						"Personal din direcția urbanism",
						"Muncă manuală, căutat prin arhive și dosare.",
						"Găsiți orice reglementare în secunde — și documentul se completează singur.",
					),
					PersonaCard(
						"Avizatori & experți tehnici",
						"Erori, versiuni contradictorii, răspundere personală.",
						"Istoric complet, semnat — se știe exact cine, ce și când a modificat.",
					),
					PersonaCard(
						"IT & achiziții publice",
						"Risc de dependență de furnizor, integrare, conformitate.",
						"Standarde deschise, datele rămân la instituție, export oricând.",
					),
				),
			),
		),

		// ── Platforma / Tech (dark) — singura sectiune unde jargonul e permis ──
		t.section(
			{ id: "platforma", class: "bg-slate-900 text-slate-200" },
			t.div(
				{ class: "max-w-6xl mx-auto px-6 py-24" },
				t.div(
					{ class: "max-w-2xl mb-10" },
					Eyebrow("Pentru colegii de la IT și achiziții", "text-cyan-400"),
					t.h2(
						{ class: "text-3xl sm:text-4xl font-bold tracking-tight text-white" },
						"Fără dependență de furnizor. Datele rămân ale instituției.",
					),
					t.p(
						{ class: "mt-4 text-lg text-slate-400 dark:text-slate-500" },
						"Formate și sisteme de coordonate standard din cadastrul și urbanismul românesc — import și export în ambele direcții.",
					),
				),
				t.div(
					{ class: "flex flex-wrap gap-3" },
					[
						"GeoPackage (.gpkg)",
						"Shapefile",
						"EPSG:3844 Stereo70",
						"EPSG:4326 WGS84",
						"PostGIS",
						"GeoJSON / MVT",
						"MCP pentru asistenți AI",
						"Găzduire în UE (Amsterdam)",
						"Export oricând",
					].map((x) =>
						t.span(
							{
								class:
									"inline-flex items-center rounded-full border border-slate-700 bg-slate-800 px-3.5 py-1.5 text-sm font-medium text-slate-200",
							},
							x,
						),
					),
				),
				t.div(
					{ class: "mt-12 grid sm:grid-cols-3 gap-8 border-t border-slate-800 pt-10" },
					[
						[
							ICONS.lock,
							"Suveranitatea datelor",
							"Bază de date dedicată, izolată. Exportați tot, oricând, în formate standard.",
						],
						[
							ICONS.clock,
							"Istoric complet",
							"Nicio modificare nu se pierde. Starea de la orice dată se reconstituie exact.",
						],
						[
							ICONS.shield,
							"Conformitate & GDPR",
							"Găzduire în Uniunea Europeană, fără cookie-uri de urmărire, termeni și politică de confidențialitate publice.",
						],
					].map(([ic, h, p]) =>
						t.div(
							t.div({ class: "text-cyan-400 mb-3" }, Icon(ic as string)),
							t.h3({ class: "font-bold text-white" }, h as string),
							t.p(
								{ class: "mt-1 text-slate-400 dark:text-slate-500 text-sm leading-relaxed" },
								p as string,
							),
						),
					),
				),
			),
		),

		// ── CTA repetat (finalul paginii) ──
		t.section(
			{ class: "max-w-6xl mx-auto px-6 py-16 text-center" },
			t.h2(
				{
					class: "text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100",
				},
				"Gata să vedeți platforma pe datele instituției dumneavoastră?",
			),
			t.div(
				{ class: "mt-6 flex flex-col sm:flex-row gap-3 justify-center" },
				t.a(
					{
						href: "mailto:office@vego.holdings?subject=Demonstrație%20Urbanism",
						class:
							"px-6 py-3.5 rounded-xl bg-cyan-700 text-white font-semibold hover:bg-cyan-800 shadow-sm",
					},
					"Programați o demonstrație",
				),
				t.a(
					{
						href: "#cere-demo",
						onclick: scroll_to_id("cere-demo"),
						class:
							"px-6 py-3.5 rounded-xl border border-slate-300 dark:border-slate-600 font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800",
					},
					"Ce parcurgem în demonstrație",
				),
			),
		),

		// ── Aplicația mobilă (Google Play) ── badge-ul oficial + QR (doar desktop — pe telefon
		// dai tap pe badge). Asset-uri STATICE din public/ — fără servicii externe la runtime.
		t.section(
			{
				// în aplicația instalată (TWA din Play / PWA) secțiunea „descarcă aplicația" n-are sens
				class:
					"border-t border-slate-200 dark:border-slate-700" + (is_standalone() ? " hidden" : ""),
			},
			t.div(
				{
					class:
						"max-w-6xl mx-auto px-6 py-12 flex flex-col sm:flex-row items-center justify-center gap-8",
				},
				t.div(
					{ class: "text-center sm:text-left" },
					t.h2(
						{ class: "text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100" },
						"Aplicația mobilă pentru teren și cetățeni",
					),
					t.p(
						{ class: "mt-2 text-sm text-slate-600 dark:text-slate-400 max-w-md" },
						"Harta, regimul urbanistic și sesizările — pe telefon. " +
							"Descarcă „Urbanism by VEGO” din Google Play sau scanează codul.",
					),
					t.a(
						{
							href: "https://play.google.com/store/apps/details?id=holdings.renda.urbanism",
							target: "_blank",
							rel: "noopener",
							class: "inline-block mt-4",
						},
						t.img({
							src: "/public/google-play-badge.png",
							alt: "Disponibil pe Google Play",
							class: "h-16 w-auto",
						}),
					),
				),
				t.img({
					src: "/public/qr-play.png",
					alt: "Cod QR — Urbanism by VEGO pe Google Play",
					class:
						"hidden sm:block w-36 h-36 rounded-xl border border-slate-200 dark:border-slate-700 bg-white p-1.5",
				}),
			),
		),

		// ── Footer ──
		t.footer(
			{ class: "border-t border-slate-200 dark:border-slate-700" },
			t.div(
				{
					class:
						"max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4",
				},
				t.div(
					{ class: "flex items-center gap-2" },
					t.img({ src: "/public/logo-192.png", alt: "", class: "w-7 h-7" }),
					t.span(
						{ class: "text-cyan-700 dark:text-cyan-300 font-extrabold tracking-tight" },
						"Urbanism",
					),
					t.span(
						{ class: "hidden md:inline text-slate-400 dark:text-slate-500 text-sm" },
						"· urbanism digital pentru administrația publică",
					),
					t.span({ class: "text-slate-400 dark:text-slate-500 text-sm ml-1" }, "· un produs"),
					t.img({ src: "/public/vego.png", alt: "VEGO", class: "h-3 w-auto opacity-80" }),
				),
				t.div(
					// flex-wrap + gap redus pe mobil: rândul are ~600px min-content — fără wrap lățea
					// DOCUMENTUL cu ~105px (bara „fantomă" din dreapta la pinch-out, feedback 24.08)
					{
						class:
							"flex items-center flex-wrap gap-x-4 gap-y-1 sm:gap-6 justify-center sm:justify-start text-sm text-slate-500 dark:text-slate-400",
					},
					t.a({ href: "#/map", class: "hover:text-slate-900" }, "Hartă"),
					t.a({ href: "#/documents", class: "hover:text-slate-900" }, "Documente"),
					t.a({ href: "#/admin", class: "hover:text-slate-900" }, "Administrare"),
					t.a({ href: "#/docs", class: "hover:text-slate-900" }, "Documentație"),
					t.a({ href: "/termeni", class: "hover:text-slate-900" }, "Termeni"),
					t.a({ href: "/confidentialitate", class: "hover:text-slate-900" }, "Confidențialitate"),
					t.a({ href: "/accesibilitate", class: "hover:text-slate-900" }, "Accesibilitate"),
				),
			),
			// Nota din footer: rolul AI-ului (argument de încredere) + operatorul.
			// Condițiile de disponibilitate/fază pilot trăiesc în /termeni, nu pe pagina de vânzare.
			t.div(
				{
					class:
						"max-w-6xl mx-auto px-6 pb-8 -mt-4 text-xs text-slate-400 dark:text-slate-500 leading-relaxed",
				},
				// Datele de identificare complete (Legea 365/2002 art. 5, Legea 31/1990 art. 74) stau în
				// /termeni#identificare — footerul poartă doar denumirea + CIF + linkul (LEGAL_CHECKLIST.md B4/C1).
				"Asistentul AI pregătește drafturi — decizia, verificarea juridică și semnătura aparțin instituției. © VEGO CONCEPT ENGINEERING SRL · CIF RO29319742 · J2011013314404 · București · ",
				t.a(
					{ href: "/termeni#identificare", class: "underline hover:text-slate-600" },
					"Date de identificare",
				),
			),
		),
	)
}
